import { useCallback, useContext, useEffect } from 'react';
import { shallowEqual, useDispatch } from 'react-redux';

import { Alert, Spacer } from '@zillow/constellation';
import { Anchor, FieldSet } from '@zillow/constellation-10';

import { PartialMessage } from '@zg-rentals/backend-styled-components';
import { debounce } from '@zg-rentals/zdash';

import { ModularListingContext } from '../../../../containers/CreateListingPage/context';
import { WizardContext } from '../../../Wizard/context';

import { useSelector } from '../../../../selectors/useSelector';

import actions from '../../../../containers/CreateListingPage/actions';

import wizardActions from '../../../Wizard/actions';

import PropertiesActions from '../../../../actions/PropertiesActions';

import listingCriteriaUtils from '../../../../utils/listingCriteriaUtils';

import { NOT_APPLICABLE } from '../../../../constants/ListingCriteriaConstants';
import { DISPLAY_SERVER_ERROR_AT } from '../../../../constants/WizardConstants';
import TimeConstants from '../../../../constants/TimeConstants';

import Criterion from '../../../../model/ListingCriteria/Criterion';
import ListingCriteria from '../../../../model/ListingCriteria';
import WizardServerError from '../../../../model/WizardServerError';

import IncomeToRentRatioFormField from './IncomeToRentRatioFormField';
import MinCreditScoreFormFieldContainer from '../../MinCreditScoreFormFieldContainer';
import WizardAlert from '../../../WizardAlert';

import { useSetListingCriteria } from '../../../../hooks/queries/listingCriteria';

import { TYPES_MAP } from '../../../../constants/ListingCriteriaConstants';

import { WizardBody, WizardFormSectionContainer, WizardHeading } from '../../../../styles/wizard';

import type { CriterionProps } from '../../../../model/ListingCriteria/Criterion';
import type { ErrorRMWProps } from '../../../../model/ErrorRMW';
import type { PartialMessageProps } from '@zg-rentals/backend-styled-components';

const FormSectionContainer = () => {
  const dispatch = useDispatch();

  const {
    reducerWizardDispatch,
    // @ts-expect-error WizardContext is not typed
    wizardState: { isFetchingData },
  } = useContext(WizardContext);

  const {
    modularListingState: { dataToBeSavedMap, isInModalState, serverErrorObj },
    reducerDispatch,
  } = useContext(ModularListingContext);

  const { currentCriteria, listingAlias } = useSelector((state) => {
    return {
      currentCriteria: state?.properties?.currentProperty?.listingCriteria?.criteria,

      listingAlias: state?.properties?.currentProperty?.listingAlias,
    };
  }, shallowEqual);

  const getCriterion = (type: string) => listingCriteriaUtils.getCriteriaByType(currentCriteria, type);

  const creditScoreCriterion = getCriterion(TYPES_MAP.CREDIT_SCORE);
  const incomeToRentRatioCriterion = getCriterion(TYPES_MAP.INCOME_TO_RENT);

  const isCreditScoreEnabled = creditScoreCriterion.isEnabled ?? true;
  const isRentToIncomeRatioEnabled = incomeToRentRatioCriterion.isEnabled ?? true;

  const creditScoreAlert = creditScoreCriterion.alert ?? null;
  const rentToIncomeRatioAlert = incomeToRentRatioCriterion.alert ?? null;

  const incomeToRentRatioMax = incomeToRentRatioCriterion.max;

  const displayAlertContainer = isCreditScoreEnabled || isRentToIncomeRatioEnabled;

  // the fields that need to be saved successfully before we close the edit modal
  const successFields = [
    ...(isRentToIncomeRatioEnabled ? [TYPES_MAP.INCOME_TO_RENT] : []),
    ...(isCreditScoreEnabled ? [TYPES_MAP.CREDIT_SCORE] : []),
  ];

  const { mutateAsync: updateListingCriteria } = useSetListingCriteria();

  useEffect(() => {
    if (isInModalState) {
      setSaveOutsideComponent();
    }
  }, []);

  useEffect(() => {
    if (isFetchingData || hasServerError()) {
      return;
    }

    if (checkIfAllFieldsHaveValues()) {
      saveDebounced();
    }
  }, [dataToBeSavedMap.values()]);

  const checkIfAllFieldsHaveValues = () => {
    return successFields.every((element) => Array.from(dataToBeSavedMap.keys()).includes(element));
  };

  const setSaveOutsideComponent = () => {
    actions.updateAnyProperty({
      reducerDispatch,
      data: {
        saveOutsideComponent: true,
      },
    });
  };

  const getDataToSave = () => {
    const dataList = [];

    for (const valueObj of dataToBeSavedMap.values()) {
      const { fieldName, value = null } = valueObj;

      // if there are no changes value will be null
      if (value !== null) {
        const criterionData = Criterion({
          ...value,
          isEnabled: true,
          type: fieldName,
        });

        dataList.push(criterionData);
      }
    }

    return dataList;
  };

  const getPartialMessagesFromAlertLinks = (links: Array<PartialMessageProps> = []) => {
    const response: Array<PartialMessageProps> = [];

    const isLastIndex = links?.length - 1;

    links?.forEach((link, index) => {
      response.push({
        isBold: link.isBold,
        text: link.text,
        url: link.url,
      });

      if (index !== isLastIndex) {
        // add space between links
        response.push({
          text: ' ',
        });
      }
    });

    return response;
  };

  const hasServerError = () => {
    return serverErrorObj.fieldName !== '';
  };

  /**
   * criterion can not be an empty string when saving
   * set to NOT_APPLICABLE if empty
   */
  const normalizeCriterion = (dataArray: Array<CriterionProps>) => {
    if (dataArray.length === 0) {
      return dataArray;
    }

    const dataArrayCopy = dataArray.slice(0);

    if (dataArrayCopy[0]['criterion'] === '') {
      dataArrayCopy[0]['criterion'] = NOT_APPLICABLE;
    }

    return dataArrayCopy;
  };

  const updateServerError = (message = '', fieldName = '') => {
    const displayAt = isInModalState ? DISPLAY_SERVER_ERROR_AT.FIELD : DISPLAY_SERVER_ERROR_AT.PAGE;

    actions.updateServerError({
      reducerDispatch,
      serverErrorObj: WizardServerError({
        displayAt,
        fieldName,
        message,
        displayMessage: message,
      }),
    });
  };

  const save = () => {
    const dataArray: Array<CriterionProps> = normalizeCriterion(getDataToSave());

    if (dataArray.length === 0) {
      actions.clearFieldDataEntry({ reducerDispatch });

      // reset save cta
      actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      return;
    }

    wizardActions.updateIsFetchingData({ reducerWizardDispatch, isFetchingData: true });

    updateListingCriteria({ listingAlias, criteria: dataArray })
      .then(() => {
        // sync current property
        dispatch(
          PropertiesActions.updateCurrentProperty({
            listingCriteria: ListingCriteria({
              isEnabled: true,
              criteria: [...currentCriteria, ...dataArray],
            }),
          }),
        );

        actions.clearFieldDataEntry({ reducerDispatch });

        // reset save cta
        actions.updateIsSaveComplete({ reducerDispatch, isSaveComplete: false });
      })
      .catch((err: { error: ErrorRMWProps }) => {
        const {
          error: { message },
        } = err;

        updateServerError(message, 'multi');
      })
      .finally(() => {
        wizardActions.updateIsFetchingData({
          reducerWizardDispatch,
          isFetchingData: false,
        });
      });
  };

  const saveDebounced = useCallback(debounce(save, TimeConstants.SELECTION_DEBOUNCE_TIME_MS), []);

  return (
    <div data-testid="FormSectionContainer">
      {serverErrorObj.fieldName === 'multi' && (
        <WizardAlert serverErrorObj={serverErrorObj} displayAt={DISPLAY_SERVER_ERROR_AT.FIELD} />
      )}

      <WizardFormSectionContainer>
        <WizardHeading level={1} textStyle="heading-sm">
          What are your financial expectations for renters?
        </WizardHeading>

        <WizardBody>
          We display these criteria on your listing and use them to help you review prospective renters, so you can
          quickly see who’s a good fit for your property.
        </WizardBody>

        {displayAlertContainer && (
          <Spacer as="div" data-testid="FormSectionContainerAlert" marginY="lg">
            {isCreditScoreEnabled && creditScoreAlert && (
              <Spacer data-testid="FormSectionContainerAlert-CreditScore" marginBottom="sm">
                <Alert
                  appearance="warning"
                  body={
                    <>
                      <Spacer marginBottom="xs">{creditScoreAlert.body}</Spacer>

                      <PartialMessage
                        linkComponent={Anchor}
                        partialMessages={getPartialMessagesFromAlertLinks(creditScoreAlert?.links ?? [])}
                      />
                    </>
                  }
                />
              </Spacer>
            )}

            {isRentToIncomeRatioEnabled && rentToIncomeRatioAlert && (
              <Spacer data-testid="FormSectionContainerAlert-RentToIncomeRatio" marginBottom="sm">
                <Alert
                  appearance="warning"
                  body={
                    <>
                      <Spacer marginBottom="xs">{rentToIncomeRatioAlert.body}</Spacer>

                      <PartialMessage
                        linkComponent={Anchor}
                        partialMessages={getPartialMessagesFromAlertLinks(rentToIncomeRatioAlert?.links ?? [])}
                      />
                    </>
                  }
                />
              </Spacer>
            )}
          </Spacer>
        )}

        {isRentToIncomeRatioEnabled && (
          <FieldSet>
            <IncomeToRentRatioFormField
              label="Minimum income-to-rent ratio"
              incomeToRentRatioMax={incomeToRentRatioMax}
            />
          </FieldSet>
        )}

        {isCreditScoreEnabled && <MinCreditScoreFormFieldContainer />}
      </WizardFormSectionContainer>
    </div>
  );
};

export default FormSectionContainer;
